import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Check, User, Target } from 'lucide-react';
import { AdvisorAvatar } from '../components/AdvisorAvatar';
import { useAuth } from '../hooks/useAuth';
import { createUserProfile, getUserProfile } from '../lib/supabase';

const defaultAdvisors = [
  {
    id: '1',
    name: 'Beatrice',
    title: 'The Strategist',
    description: 'Hyper-rational strategist who thinks in frameworks and first principles.',
  },
  {
    id: '2',
    name: 'Samuel',
    title: 'The Mentor',
    description: 'Empathetic mentor focused on human elements and personal growth.',
  },
  {
    id: '3',
    name: 'Leo',
    title: 'The Innovator',
    description: 'Creative innovator who challenges assumptions and thinks outside the box.',
  },
  {
    id: '4',
    name: 'Marcus',
    title: 'The Stoic',
    description: 'Philosopher grounded in Stoicism, providing wisdom on mindset and resilience.',
  }
];

const focusAreas = [
  'Career Growth', 'Business Strategy', 'Leadership', 'Innovation',
  'Personal Development', 'Financial Planning', 'Health & Wellness',
  'Relationships', 'Creativity', 'Technology'
];

export const OnboardingFlow: React.FC = () => {
  const navigate = useNavigate();
  const { user, emailVerified, refreshProfile, loading } = useAuth();
  const [isCheckingProfile, setIsCheckingProfile] = useState(true);
  const [step, setStep] = useState(1);
  
  const userFullName = user?.user_metadata?.full_name || "User";
  
  const [formData, setFormData] = useState({
    preferredName: userFullName,
    selectedFocusAreas: [] as string[]
  });

  // Check if user has already completed onboarding
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!loading && user && emailVerified) {
        try {
          const { data: profile } = await getUserProfile(user.id);
          if (profile && profile.onboarding_completed) {
            // User has already completed onboarding, redirect to boardroom
            navigate('/boardroom');
            return;
          }
        } catch (error) {
          console.error('Error checking onboarding status:', error);
        }
      }
      setIsCheckingProfile(false);
    };

    checkOnboardingStatus();
  }, [user, emailVerified, loading, navigate]);

  // Redirect if not authenticated or email not verified
  useEffect(() => {
    if (!loading && (!user || !emailVerified)) {
      navigate('/');
    }
  }, [loading, user, emailVerified, navigate]);

  // Show loading while checking profile
  if (loading || isCheckingProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full border-4 border-orange-400/20 border-t-orange-400 animate-spin mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render if not authenticated
  if (!user || !emailVerified) {
    return null;
  }

  const handleNext = async () => {
    if (step < 2) {
      setStep(step + 1);
    } else {
      // Complete onboarding by creating user profile
      if (user) {
        try {
          await createUserProfile(user.id, formData.preferredName, formData.selectedFocusAreas);
          await refreshProfile();
          navigate('/boardroom');
        } catch (error) {
          console.error('Failed to create user profile:', error);
        }
      }
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    } else {
      navigate('/');
    }
  };

  const toggleFocusArea = (area: string) => {
    setFormData(prev => ({
      ...prev,
      selectedFocusAreas: prev.selectedFocusAreas.includes(area)
        ? prev.selectedFocusAreas.filter(a => a !== area)
        : [...prev.selectedFocusAreas, area]
    }));
  };

  const canProceed = step === 1 
    ? formData.preferredName.trim().length > 0 
    : formData.selectedFocusAreas.length > 0;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Main Content - Scrollable */}
      <div className="flex-1 flex items-center justify-center p-4 pb-24">
        <div className="w-full max-w-4xl">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
                className="bg-black/40 backdrop-blur-sm rounded-3xl p-6 sm:p-8 md:p-12 border border-white/10"
              >
                <div className="text-center mb-6 sm:mb-8">
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.6 }}
                    className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-r from-orange-400 to-purple-500 mb-4 sm:mb-6"
                  >
                    <User className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                  </motion.div>
                  <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 bg-gradient-to-r from-orange-400 to-purple-400 bg-clip-text text-transparent">
                    Welcome to Your Board
                  </h1>
                  <p className="text-lg sm:text-xl text-white/80 mb-6 sm:mb-8 px-2">
                    Let's personalize your advisory experience
                  </p>
                </div>

                <div className="max-w-md mx-auto">
                  <label className="block text-white font-semibold mb-3 text-base sm:text-lg">
                    What would you prefer to be called?
                  </label>
                  <p className="text-white/60 text-sm mb-4">
                    This can be different from your full name - whatever feels comfortable for your sessions
                  </p>
                  <input
                    type="text"
                    value={formData.preferredName}
                    onChange={(e) => setFormData(prev => ({ ...prev, preferredName: e.target.value }))}
                    placeholder="e.g., John, Johnny, J, etc."
                    className="w-full px-4 sm:px-6 py-3 sm:py-4 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/60 text-base sm:text-lg focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-300"
                    autoFocus
                  />
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
                className="bg-black/40 backdrop-blur-sm rounded-3xl p-6 sm:p-8 md:p-12 border border-white/10"
              >
                <div className="text-center mb-6 sm:mb-8">
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.6 }}
                    className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-r from-orange-400 to-purple-500 mb-4 sm:mb-6"
                  >
                    <Target className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                  </motion.div>
                  <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 bg-gradient-to-r from-orange-400 to-purple-400 bg-clip-text text-transparent">
                    What Are Your Focus Areas?
                  </h1>
                  <p className="text-lg sm:text-xl text-white/80 mb-6 sm:mb-8 px-2">
                    Select areas where you'd like guidance (choose 3-5)
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
                  {focusAreas.map((area) => (
                    <motion.button
                      key={area}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => toggleFocusArea(area)}
                      className={`p-3 sm:p-4 rounded-2xl border-2 transition-all duration-300 ${
                        formData.selectedFocusAreas.includes(area)
                          ? 'bg-gradient-to-r from-orange-500 to-purple-500 border-transparent text-white'
                          : 'bg-white/5 border-white/20 text-white/80 hover:bg-white/10 hover:border-white/40'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm sm:text-base">{area}</span>
                        {formData.selectedFocusAreas.includes(area) && (
                          <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                        )}
                      </div>
                    </motion.button>
                  ))}
                </div>

                {/* Meet the Board Preview */}
                <div className="text-center mb-6 sm:mb-8">
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">Meet Your Default Advisory Board</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                    {defaultAdvisors.map((advisor) => (
                      <motion.div
                        key={advisor.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/10"
                      >
                        <AdvisorAvatar name={advisor.name} size="large" className="mx-auto mb-3 sm:mb-4" />
                        <h4 className="text-base sm:text-lg font-bold text-white mb-2">{advisor.name}</h4>
                        <p className="text-orange-400 font-medium mb-2 sm:mb-3 text-sm sm:text-base">{advisor.title}</p>
                        <p className="text-white/70 text-xs sm:text-sm">{advisor.description}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Fixed Navigation Footer */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-sm border-t border-white/10 p-4 sm:p-6">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleBack}
            className="flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 transition-all duration-300"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="text-sm sm:text-base">Back</span>
          </motion.button>

          {/* Step Indicators */}
          <div className="flex gap-2">
            {[1, 2].map((stepNumber) => (
              <div
                key={stepNumber}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  stepNumber === step
                    ? 'bg-gradient-to-r from-orange-400 to-purple-400'
                    : stepNumber < step
                    ? 'bg-green-400'
                    : 'bg-white/20'
                }`}
              />
            ))}
          </div>

          {/* Next/Complete Button */}
          <motion.button
            whileHover={{ scale: canProceed ? 1.05 : 1 }}
            whileTap={{ scale: canProceed ? 0.95 : 1 }}
            onClick={handleNext}
            disabled={!canProceed}
            className={`flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-all duration-300 ${
              canProceed
                ? 'bg-gradient-to-r from-orange-500 to-purple-500 text-white hover:from-orange-400 hover:to-purple-400 shadow-lg'
                : 'bg-white/10 text-white/50 cursor-not-allowed'
            }`}
          >
            <span className="text-sm sm:text-base">{step === 2 ? 'Enter the Boardroom' : 'Next'}</span>
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
          </motion.button>
        </div>

        {/* Progress Context */}
        <div className="text-center mt-3">
          <p className="text-white/60 text-sm">
            {step === 1 
              ? 'Step 1 of 2: Personal Preferences' 
              : `Step 2 of 2: Focus Areas (${formData.selectedFocusAreas.length} selected)`
            }
          </p>
        </div>
      </div>
    </div>
  );
};